// Sperrzustand eines Features für eine Oberfläche, plus Texte für Pille,
// Tooltip und Dialog.
//
// Die neue Lock-UX (LockBadge, LockedSection, UpgradeInline, Upgrade-Dialog)
// gilt nur in enforced. In legacy und preview ist locked deshalb immer false.
// Die fünf Bestands-Gates laufen dort weiter über ihren Boolean im
// PaymentContext und die heutige Paywall, die vier neuen Gates sind dort frei.
//
// Paketnamen erscheinen nur, wenn ein Plan-Payment da ist (echt oder
// simuliert). Ohne Plan heißt die Pille neutral „Gesperrt".
import { useCallback, useMemo } from 'react'

import trackEvents from '../../constants/track-events'
import { trackEvent } from '../../helpers/analytics'
import { MODES } from './entitlements'
import { useEntitlements, useFeature } from './hooks'
import { PLAN_ORDER } from './plans'
import { usePricingDialog } from './pricing-dialog-context'

const LANDINGPAGE_URL = 'https://landingpage.aivy.app'

/**
 * Preisseite der Landingpage für „Pakete vergleichen". Englisch nur, wenn
 * die Oberfläche auf Englisch steht, sonst Deutsch. Deutsch ist die
 * Default-Sprache der LP (gatsby-plugin-react-i18next) und liegt ohne
 * Präfix unter /, /de/ gibt 404.
 *
 * @param {string} [language] i18n.language
 */
export const getPricingCompareUrl = (language) => {
  const isEnglish = String(language || '')
    .toLowerCase()
    .startsWith('en')
  return `${LANDINGPAGE_URL}/${isEnglish ? 'en/' : ''}#pricing`
}

const LAST_PLAN = PLAN_ORDER[PLAN_ORDER.length - 1]

// Stabiler Fallback, damit useMemo nicht bei jedem Render neu rechnet
const NO_PLANS = Object.freeze([])

const planName = (t, planId) => t(`pricing.plans.${planId}`)

// Paketnamen als Aufzählung, etwa „Growth und Scale"
const joinPlanNames = (t, planIds) => {
  const names = planIds.map((planId) => planName(t, planId))
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')}${t('pricing.lock.and')}${
    names[names.length - 1]
  }`
}

/**
 * Pille und Hinweis zu einem gesperrten Feature, rein und ohne React.
 *
 * label ist der Text der Pille. Bei Zusatz im eigenen Paket „Zusatz", sonst
 * das kleinste Paket, das das Feature enthält. includedText ist der Satz für
 * Tooltip und Dialog, etwa „Enthalten ab Growth" oder „In Growth zubuchbar,
 * enthalten ab Scale". Ohne Plan, und wenn das eigene Paket das Feature laut
 * Katalog enthält (Individualvertrag ohne das Produkt), bleibt es neutral.
 *
 * @param {{ t: Function, planId?: string|null, requiredPlan?: string|null,
 *   isAddon?: boolean, includedIn?: string[], addonIn?: string[] }} params
 * @returns {{ label: string, includedText: string, showPlanNames: boolean }}
 */
export const describeFeatureLock = ({
  t,
  planId = null,
  requiredPlan = null,
  isAddon = false,
  includedIn = [],
  addonIn = []
}) => {
  const neutral = {
    label: t('pricing.lock.locked'),
    includedText: t('pricing.lock.tooltip'),
    showPlanNames: false
  }

  if (!planId) return neutral

  if (isAddon) {
    return {
      label: t('pricing.lock.addon'),
      includedText: t('pricing.lock.addon_in', {
        addon: planName(t, planId)
      }),
      showPlanNames: true
    }
  }

  if (!requiredPlan || includedIn.includes(planId)) return neutral

  const plan = planName(t, requiredPlan)
  const onlyLast = requiredPlan === LAST_PLAN

  let includedText
  if (addonIn.length > 0) {
    const addon = joinPlanNames(t, addonIn)
    includedText = onlyLast
      ? t('pricing.lock.addon_included_in', { addon, plan })
      : t('pricing.lock.addon_from', { addon, plan })
  } else {
    includedText = onlyLast
      ? t('pricing.lock.included_in', { plan })
      : t('pricing.lock.included_from', { plan })
  }

  return { label: plan, includedText, showPlanNames: true }
}

/**
 * Gate eines Features (Keys aus FEATURES in plans.js).
 *
 * locked ist nur in enforced true. readOnly heißt gesperrt, aber schon in
 * Nutzung (inUse). Das läuft weiter und bleibt lesbar, Abschalten geht immer.
 * open() öffnet den Upgrade-Dialog des globalen Hosts. Innerhalb eines Modals
 * oder Slideovers nicht open() nutzen, sondern UpgradeInline, weil HeadlessUI
 * 2.1 Geschwister-Dialoge nicht sauber stapelt.
 *
 * @param {string} featureKey
 * @param {{ source?: string, inUse?: boolean }} [options]
 * @returns {{ feature: string, mode: string, enforced: boolean,
 *   locked: boolean, readOnly: boolean, requiredPlan: string|null,
 *   isAddon: boolean, planId: string|null, includedIn: string[],
 *   addonIn: string[], open: () => void }}
 */
export const useFeatureGate = (featureKey, { source, inUse = false } = {}) => {
  const state = useFeature(featureKey)
  const { plan, isSimulated } = useEntitlements()
  const dialog = usePricingDialog()
  const openFeatureDialog = dialog?.openFeatureDialog

  const enforced = state.mode === MODES.ENFORCED
  const locked = enforced && !!state.locked
  const readOnly = locked && !!inUse
  const planId = plan?.id ?? null
  const includedIn = state.includedIn || NO_PLANS
  const addonIn = state.addonIn || NO_PLANS
  const isAddon = !!planId && addonIn.includes(planId)
  const requiredPlan = state.minPlan ?? null

  // Jeder Klick auf eine Sperre der neuen UX läuft über open(), also Pille,
  // „Freischalten" und gesperrte Optionen. In legacy und preview öffnet der
  // Host die heutige Paywall, dort bleibt es bei PRESSED_PAYWALL.
  const open = useCallback(() => {
    if (enforced) {
      trackEvent(trackEvents.PRICING_LOCK_PRESSED, {
        feature: featureKey,
        source: source ?? null,
        in_use: !!inUse,
        plan: planId,
        mode: state.mode,
        simulated: !!isSimulated
      })
    }
    openFeatureDialog?.(featureKey, { source, inUse: !!inUse })
  }, [
    openFeatureDialog,
    featureKey,
    source,
    inUse,
    enforced,
    planId,
    state.mode,
    isSimulated
  ])

  return useMemo(
    () => ({
      feature: featureKey,
      mode: state.mode,
      enforced,
      locked,
      readOnly,
      requiredPlan,
      isAddon,
      planId,
      includedIn,
      addonIn,
      open
    }),
    [
      featureKey,
      state.mode,
      enforced,
      locked,
      readOnly,
      requiredPlan,
      isAddon,
      planId,
      includedIn,
      addonIn,
      open
    ]
  )
}
